import {
  CollateralHealthStatusEnum,
  IndicatorDotVariants,
  OTCCollateralizationModeEnum,
  format,
  formatPercent,
  prettyName,
  toBig,
} from '@talos/kyoko';
import Big from 'big.js';
import type { DefaultTheme } from 'styled-components';
import {
  CounterpartyMonitoringProductType,
  isCounterpartyMonitoringCollateralizedDeal,
  isCounterpartyMonitoringLoan,
  type CounterpartyMonitoringRow,
} from './counterpartyMonitoringRows';

export const COLLATERAL_HEALTH_ZONE_COUNT = 4;

export interface CollateralHealthThreshold {
  label: string;
  value: string | undefined;
}

export interface CollateralHealthMarkerPlacement {
  /** 0 = extrapolated→liquidation … 3 = release→extrapolated healthy bound */
  zoneIndex: number;
  /** 0 at the unhealthy edge of the zone, 1 at the healthy edge */
  withinZoneRatio: number;
}

function isCollatMode(row: CounterpartyMonitoringRow | undefined) {
  if (!isCounterpartyMonitoringCollateralizedDeal(row)) {
    return false;
  }

  return (
    row.productType === CounterpartyMonitoringProductType.Repo ||
    row.collateralStatusDetails?.CollateralizationMode === OTCCollateralizationModeEnum.Percent
  );
}

export function formatCollateralHealthRatio(value: string | undefined, row: CounterpartyMonitoringRow | undefined) {
  const formatted = formatPercent(toBig(value), 0);
  return formatted ? `${formatted}% ${isCollatMode(row) ? 'Collat' : 'LTV'}` : undefined;
}

function getFirstPriceResult(row: CounterpartyMonitoringRow | undefined) {
  return isCounterpartyMonitoringCollateralizedDeal(row) ? row.collateralStatusDetails?.PriceResults?.[0] : undefined;
}

export function getFormattedCollateralRatio(row: CounterpartyMonitoringRow | undefined) {
  return formatCollateralHealthRatio(getFirstPriceResult(row)?.Ratio, row);
}

/** Display suffix for the primary contractual price source. */
export function getCollateralContractualSource(row: CounterpartyMonitoringRow | undefined): 'Contractual' | undefined {
  return isCounterpartyMonitoringCollateralizedDeal(row) ? 'Contractual' : undefined;
}

export function getCollateralHealthThresholds(row: CounterpartyMonitoringRow | undefined): CollateralHealthThreshold[] {
  if (row && 'collateralStatus' in row && row.collateralStatus === CollateralHealthStatusEnum.Unsecured) {
    return [];
  }

  if (row?.productType === CounterpartyMonitoringProductType.Repo) {
    return [
      { label: 'Close Out', value: row.closeOutMarginPct },
      { label: 'Margin Call', value: row.marginCallPct },
      { label: 'Maintenance', value: row.maintenanceMarginPct },
      { label: 'Release', value: row.marginReleasePct },
    ];
  }

  if (isCounterpartyMonitoringLoan(row)) {
    return [
      { label: 'Liquidation', value: row.thresholdsLiquidation },
      { label: 'Margin Call', value: row.thresholdsMarginCall },
      { label: 'Minimum', value: row.thresholdsMinimum },
      { label: 'Release', value: row.thresholdsCollateralRelease },
    ];
  }

  return [];
}

function clamp(value: Big, min: number, max: number) {
  return value.lt(min) ? Big(min) : value.gt(max) ? Big(max) : value;
}

function getCollateralHealthBounds(row: CounterpartyMonitoringRow | undefined) {
  if (row?.productType === CounterpartyMonitoringProductType.Repo) {
    return {
      liquidation: toBig(row.closeOutMarginPct),
      marginCall: toBig(row.marginCallPct),
      minimum: toBig(row.maintenanceMarginPct),
      release: toBig(row.marginReleasePct),
    };
  }

  if (isCounterpartyMonitoringLoan(row)) {
    return {
      liquidation: toBig(row.thresholdsLiquidation),
      marginCall: toBig(row.thresholdsMarginCall),
      minimum: toBig(row.thresholdsMinimum),
      release: toBig(row.thresholdsCollateralRelease),
    };
  }

  return { liquidation: undefined, marginCall: undefined, minimum: undefined, release: undefined };
}

/**
 * Unhealthy→healthy zone edges: extrapolatedLeft, Liquidation, MarginCall, Release, extrapolatedRight.
 * Minimum/Maintenance is required for monotonicity but is not a zone edge.
 */
function getOrderedCollateralHealthBounds(
  row: CounterpartyMonitoringRow | undefined
): [Big, Big, Big, Big, Big] | undefined {
  const { liquidation, marginCall, minimum, release } = getCollateralHealthBounds(row);
  if (!liquidation || !marginCall || !minimum || !release) {
    return undefined;
  }

  const useCollatMode = isCollatMode(row);
  const isMonotonic = useCollatMode
    ? liquidation.lt(marginCall) && marginCall.lt(minimum) && minimum.lt(release)
    : liquidation.gt(marginCall) && marginCall.gt(minimum) && minimum.gt(release);

  if (!isMonotonic) {
    return undefined;
  }

  const liquidationToMarginCallSpan = marginCall.minus(liquidation);
  const extrapolatedLeft = liquidation.minus(liquidationToMarginCallSpan);
  const marginCallToReleaseSpan = release.minus(marginCall);
  const extrapolatedRight = release.plus(marginCallToReleaseSpan);

  return [extrapolatedLeft, liquidation, marginCall, release, extrapolatedRight];
}

function isAtOrBeyondHealthyEdge(value: Big, edge: Big, useCollatMode: boolean) {
  return useCollatMode ? value.gte(edge) : value.lte(edge);
}

function isAtOrBeyondUnhealthyEdge(value: Big, edge: Big, useCollatMode: boolean) {
  return useCollatMode ? value.lte(edge) : value.gte(edge);
}

function getWithinZoneRatio(value: Big, zoneStart: Big, zoneEnd: Big, useCollatMode: boolean) {
  const span = zoneEnd.minus(zoneStart);
  if (span.eq(0)) {
    return Big(0);
  }

  const progress = useCollatMode
    ? value.minus(zoneStart).div(span)
    : zoneStart.minus(value).div(zoneStart.minus(zoneEnd));
  return clamp(progress, 0, 1);
}

export function getCollateralHealthMarkerPlacement(
  row: CounterpartyMonitoringRow | undefined,
  ratio: string | undefined = getFirstPriceResult(row)?.Ratio
): CollateralHealthMarkerPlacement | undefined {
  const orderedBounds = getOrderedCollateralHealthBounds(row);
  const value = toBig(ratio);

  if (!orderedBounds || !value) {
    return undefined;
  }

  const useCollatMode = isCollatMode(row);
  const [extrapolatedLeft, liquidation, , , extrapolatedRight] = orderedBounds;

  if (isAtOrBeyondUnhealthyEdge(value, extrapolatedLeft, useCollatMode)) {
    return { zoneIndex: 0, withinZoneRatio: 0 };
  }

  if (isAtOrBeyondHealthyEdge(value, extrapolatedRight, useCollatMode)) {
    return { zoneIndex: COLLATERAL_HEALTH_ZONE_COUNT - 1, withinZoneRatio: 1 };
  }

  // Liquidation is the healthy edge of zone 0; values at/beyond liquidation stay in zone 0.
  if (isAtOrBeyondUnhealthyEdge(value, liquidation, useCollatMode)) {
    return {
      zoneIndex: 0,
      withinZoneRatio: Number(getWithinZoneRatio(value, extrapolatedLeft, liquidation, useCollatMode).toFixed(4)),
    };
  }

  for (let zoneIndex = 1; zoneIndex < COLLATERAL_HEALTH_ZONE_COUNT; zoneIndex++) {
    const zoneStart = orderedBounds[zoneIndex];
    const zoneEnd = orderedBounds[zoneIndex + 1];
    const inZone = useCollatMode
      ? value.gte(zoneStart) && value.lt(zoneEnd)
      : value.lte(zoneStart) && value.gt(zoneEnd);

    if (inZone) {
      return {
        zoneIndex,
        withinZoneRatio: Number(getWithinZoneRatio(value, zoneStart, zoneEnd, useCollatMode).toFixed(4)),
      };
    }
  }

  return undefined;
}

export function canRenderCollateralHealthBar(row: CounterpartyMonitoringRow | undefined) {
  return getOrderedCollateralHealthBounds(row) != null;
}

export interface CollateralShortfallView {
  shortfall: string;
  shortfallCollateral: string | undefined;
  shortfallCurrency: string | undefined;
  shortfallPctMTA: string | undefined;
  minTransferAmount: string | undefined;
  minTransferAmountCurrency: string | undefined;
  collateralAsset: string | undefined;
  status: CollateralHealthStatusEnum | undefined;
}

export function hasPrimaryCollateralShortfall(row: CounterpartyMonitoringRow | undefined): boolean {
  if (!isCounterpartyMonitoringCollateralizedDeal(row)) {
    return false;
  }

  const shortfall = getFirstPriceResult(row)?.Shortfall;
  return shortfall != null && shortfall !== '';
}

export function getPrimaryCollateralShortfallView(
  row: CounterpartyMonitoringRow | undefined
): CollateralShortfallView | undefined {
  if (!hasPrimaryCollateralShortfall(row) || !isCounterpartyMonitoringCollateralizedDeal(row)) {
    return undefined;
  }

  const details = row.collateralStatusDetails;
  const priceResult = getFirstPriceResult(row);
  if (!details || !priceResult?.Shortfall) {
    return undefined;
  }

  // Backend coalesces absent agreement MTA to "0" — treat as missing before consumers see it.
  const hasMta = hasDefinedMinTransferAmount(details.MinTransferAmount);

  return {
    shortfall: priceResult.Shortfall,
    shortfallCollateral: priceResult.ShortfallCollateral,
    shortfallCurrency: details.ShortfallCurrency,
    shortfallPctMTA: hasMta ? priceResult.ShortfallPctMTA : undefined,
    minTransferAmount: hasMta ? details.MinTransferAmount : undefined,
    minTransferAmountCurrency: hasMta ? details.MinTransferAmountCurrency : undefined,
    collateralAsset: details.CollateralAsset,
    status: priceResult.HealthStatus,
  };
}

/** Backend sends this already expressed as a percentage of MTA — do not scale it. */
export function formatShortfallPctOfMta(shortfallPctMTA: string | undefined): string | undefined {
  const pct = toBig(shortfallPctMTA);
  if (pct == null) {
    return undefined;
  }
  const formatted = format(pct.toFixed(0));
  return formatted === '' ? undefined : `${formatted}%`;
}

/** Treat 0 as missing — backend coalesces absent MTA to 0. */
export function hasDefinedMinTransferAmount(minTransferAmount: string | null | undefined): minTransferAmount is string {
  const amount = toBig(minTransferAmount);
  return amount != null && amount.gt(0);
}

/** Map backend `"spot"` → `"Spot Price"`. */
export function formatCollateralPriceLabel(priceLabel: string | null | undefined): string {
  if (priceLabel == null || priceLabel === '') {
    return '-';
  }

  return priceLabel.toLowerCase() === 'spot' ? 'Spot Price' : priceLabel;
}

/** Full MinTransferAmount expressed as a percentage of itself. */
const FULL_MTA_PCT = 100;

/**
 * Accent for shortfall % / amount.
 * Liquidation → red. Margin Call → yellow when pct of MTA is missing or >= 100% of MTA.
 */
export function getShortfallAccentColor({
  shortfallPctMTA,
  status,
  theme,
}: {
  shortfallPctMTA: string | undefined;
  status: CollateralHealthStatusEnum | undefined;
  theme: DefaultTheme;
}): string {
  if (status === CollateralHealthStatusEnum.LiquidationRisk) {
    return theme.colors.red.lighten;
  }

  if (status === CollateralHealthStatusEnum.MarginCall) {
    const pct = toBig(shortfallPctMTA);
    if (!pct || !pct.lt(FULL_MTA_PCT)) {
      return theme.colors.yellow.lighten;
    }
  }

  return theme.colorTextImportant;
}

const collateralHealthStatusVariants: Record<CollateralHealthStatusEnum, IndicatorDotVariants> = {
  [CollateralHealthStatusEnum.Unsecured]: IndicatorDotVariants.Default,
  [CollateralHealthStatusEnum.Release]: IndicatorDotVariants.Positive,
  [CollateralHealthStatusEnum.Healthy]: IndicatorDotVariants.Positive,
  // Nearing Call stays green — still technically healthy (MC → Minimum/Maintenance).
  [CollateralHealthStatusEnum.Warning]: IndicatorDotVariants.Positive,
  [CollateralHealthStatusEnum.MarginCall]: IndicatorDotVariants.Warning,
  [CollateralHealthStatusEnum.LiquidationRisk]: IndicatorDotVariants.Negative,
};

export function getStatusVariant(status: CollateralHealthStatusEnum | undefined): IndicatorDotVariants {
  return status ? collateralHealthStatusVariants[status] : IndicatorDotVariants.Default;
}

export function getCollateralHealthStatusDisplayName(status: CollateralHealthStatusEnum | undefined): string {
  if (status == null) {
    return '';
  }

  switch (status) {
    case CollateralHealthStatusEnum.LiquidationRisk:
      return 'Liquidation';
    case CollateralHealthStatusEnum.Release:
      return 'Excess';
    case CollateralHealthStatusEnum.Warning:
      return 'Nearing Call';
    case CollateralHealthStatusEnum.Unsecured:
    case CollateralHealthStatusEnum.Healthy:
    case CollateralHealthStatusEnum.MarginCall:
      return prettyName(status);
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function getStatusColor(status: CollateralHealthStatusEnum | undefined, theme: DefaultTheme) {
  switch (getStatusVariant(status)) {
    case IndicatorDotVariants.Positive:
      return theme.colors.green.lighten;
    case IndicatorDotVariants.Warning:
      return theme.colors.yellow.lighten;
    case IndicatorDotVariants.Negative:
      return theme.colors.red.lighten;
    default:
      return theme.colorTextMuted;
  }
}

/** Primary ratios use status color except green (healthy) stays important text. Secondary stays gray 090. */
export function getCollateralRatioTextColor({
  isPrimary,
  status,
  theme,
}: {
  isPrimary: boolean;
  status: CollateralHealthStatusEnum | undefined;
  theme: DefaultTheme;
}): string {
  if (!isPrimary) {
    return theme.colors.gray['090'];
  }

  if (getStatusVariant(status) === IndicatorDotVariants.Positive) {
    return theme.colorTextImportant;
  }

  return getStatusColor(status, theme);
}

export function getCollateralHealthZoneColor(zoneIndex: number, theme: DefaultTheme): string {
  switch (zoneIndex) {
    case 0:
      return theme.colors.red.lighten;
    case 1:
      return theme.colors.yellow.lighten;
    case 2:
    case 3:
      return theme.colors.green.lighten;
    default:
      return theme.colorTextMuted;
  }
}

export function getCollateralHealthZoneColors(activeZoneIndex: number | undefined, theme: DefaultTheme): string[] {
  const hues = [theme.colors.red, theme.colors.yellow, theme.colors.green, theme.colors.green] as const;

  return hues.map((hue, zoneIndex) => (zoneIndex === activeZoneIndex ? hue.lighten : hue.dim));
}

export type CollateralHealthMarkerRole = 'above' | 'below';

/** Index 0 = above; later = below. */
export function getCollateralHealthMarkerRole(index: number): CollateralHealthMarkerRole {
  return index === 0 ? 'above' : 'below';
}

export function getCollateralHealthMarkerColor({
  isPrimary,
  status,
  zoneIndex,
  theme,
}: {
  isPrimary: boolean;
  status: CollateralHealthStatusEnum | undefined;
  zoneIndex?: number;
  theme: DefaultTheme;
}): string {
  if (!isPrimary) {
    return theme.colors.gray['090'];
  }

  /** Warning has no zone — use status color. */
  if (status === CollateralHealthStatusEnum.Warning) {
    return getStatusColor(status, theme);
  }

  if (zoneIndex != null) {
    return getCollateralHealthZoneColor(zoneIndex, theme);
  }

  return getStatusColor(status, theme);
}
